import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, Brain, CheckCircle2, CircleAlert, ExternalLink, FileText, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ago, errMsg, inr, KIND_LABELS } from "@/lib/api";
import { Loading, ScoreRing, StatusPill } from "@/components/common";

const WEIGHTS = { skills: 35, role: 25, experience: 25, industry: 10, seniority: 5 };
const ICON = { pass: [CheckCircle2, "text-brand"], review: [CircleAlert, "text-warn"], fail: [XCircle, "text-destructive"] };

function Breakdown({ b }) {
  return (
    <div className="space-y-3" data-testid="score-breakdown">
      {Object.entries(WEIGHTS).map(([k, w]) => (
        <div key={k}>
          <div className="mb-1 flex justify-between text-xs"><span className="capitalize">{k} <span className="text-muted-foreground">· {w}%</span></span><span className="font-mono">{b?.[k] ?? 0}/{w}</span></div>
          <div className="h-1.5 rounded-full bg-muted"><div className="h-1.5 rounded-full bg-brand transition-[width] duration-700" style={{ width: `${((b?.[k] ?? 0) / w) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Docs({ jid, docs, reload }) {
  const [busy, setBusy] = useState("");
  const nav = useNavigate();
  const gen = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/jobs/${jid}/documents`, { kind });
      toast[data.status === "blocked" ? "warning" : "success"](data.status === "blocked" ? "Quality check blocked unsupported claims" : "Draft ready for your review");
      nav(`/studio/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); reload(); } finally { setBusy(""); }
  };
  return (
    <section className="panel p-6" data-testid="job-documents-panel">
      <p className="eyebrow mb-4">Document studio</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(KIND_LABELS).map(([k, l]) => (
          <Button key={k} variant={k === "resume" ? "default" : "outline"} disabled={!!busy} onClick={() => gen(k)} data-testid={`generate-${k}-button`} className="justify-start">
            {busy === k ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Generate {l.toLowerCase()}</Button>
        ))}
      </div>
      {docs.length > 0 && (
        <ul className="mt-5 divide-y border-t" data-testid="job-documents-list">
          {docs.map((d) => (
            <li key={d.id}><Link to={`/studio/${d.id}`} className="flex items-center justify-between gap-2 py-3 text-sm hover:text-brand" data-testid={`job-doc-${d.id}`}>
              <span>{KIND_LABELS[d.kind]} · v{d.version} <span className="text-muted-foreground">· {ago(d.created_at)}</span></span><StatusPill status={d.status} /></Link></li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const load = () => api.get(`/jobs/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <Loading />;
  const { job, match: m, application } = d;
  const analyze = async () => {
    setAnalyzing(true);
    try { await api.post(`/jobs/${id}/analyze`); toast.success("Requirements re-analysed with AI"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setAnalyzing(false); }
  };
  const save = async () => {
    try { await api.post("/applications", { job_id: id }); toast.success("Saved to tracker"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-jobs"><ArrowLeft className="h-4 w-4" />All matches</Link>
      <section className="panel flex flex-col gap-6 p-6 sm:flex-row sm:items-center fade-up">
        {m && <ScoreRing score={m.score} size={96} testId="job-detail-score" />}
        <div className="flex-1 space-y-1">
          <p className="eyebrow">{job.source_type} · {job.status} · seen {ago(job.last_seen_at)}</p>
          <h1 className="text-2xl font-bold sm:text-3xl" data-testid="job-detail-title">{job.title}</h1>
          <p className="text-muted-foreground">{job.company} · {job.location || "Location not stated"}</p>
          <p className="text-sm">{job.salary_min == null && job.salary_max == null ? <span className="text-warn">Salary not published</span>
            : `${job.salary_currency || ""} ${inr(job.salary_min)}${job.salary_max ? `–${inr(job.salary_max)}` : "+"} / ${job.salary_period} · ${job.salary_type || "type unknown"}`}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button asChild data-testid="job-original-link"><a href={job.apply_url || job.url} target="_blank" rel="noopener noreferrer">Open original posting <ExternalLink className="ml-2 h-4 w-4" /></a></Button>
          {application ? <Button variant="outline" asChild data-testid="job-view-in-tracker"><Link to="/tracker">In tracker · {application.status}</Link></Button>
            : <Button variant="outline" onClick={save} data-testid="job-save-button"><Bookmark className="mr-2 h-4 w-4" />Save to tracker</Button>}
        </div>
      </section>

      {m && (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <section className="panel space-y-6 p-6">
            <div className="flex items-center justify-between"><p className="eyebrow">Score breakdown</p>
              <Button size="sm" variant="ghost" onClick={analyze} disabled={analyzing} data-testid="analyze-job-button">
                {analyzing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Brain className="mr-1 h-3.5 w-3.5" />}Analyse with AI</Button></div>
            <Breakdown b={m.breakdown} />
            <p className="text-xs text-muted-foreground">Requirements read by: {m.requirements_method === "ai" ? `AI (${job.requirements?.model})` : "keyword rules"}</p>
          </section>
          <section className="panel space-y-5 p-6">
            <div><p className="eyebrow mb-3">Hard filters</p>
              <ul className="space-y-2" data-testid="job-filters">{m.filters.map((f, i) => {
                const [Icon, c] = ICON[f.result];
                return <li key={i} className="flex gap-2 text-sm"><Icon className={`mt-0.5 h-4 w-4 shrink-0 ${c}`} /><span><span className="capitalize font-medium">{f.check.replace("_", " ")}:</span> {f.reason}</span></li>;
              })}</ul></div>
            <div><p className="eyebrow mb-2">Why this score</p><ul className="list-disc space-y-1 pl-5 text-sm" data-testid="job-reasons">{m.reasons.map((r) => <li key={r}>{r}</li>)}</ul></div>
            <div className="flex flex-wrap gap-1.5" data-testid="job-skills">
              {m.matched_skills.map((s) => <span key={s} className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400">{s}</span>)}
              {m.missing_skills.map((s) => <span key={s} className="rounded-full bg-rose-500/10 px-2 py-0.5 text-xs text-rose-700 dark:text-rose-400" data-testid="missing-skill">missing: {s}</span>)}
            </div>
          </section>
        </div>
      )}

      <Docs jid={id} docs={d.documents} reload={load} />

      <section className="panel p-6">
        <button className="eyebrow" onClick={() => setShowDesc(!showDesc)} data-testid="toggle-description">{showDesc ? "Hide" : "Show"} job description</button>
        {showDesc && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{job.description || "No description provided."}</p>}
      </section>
    </div>
  );
}
